import * as React from "react";
import type { AppContextMenuState } from "./AppContextualMenu.types";
import { AppMenuPopover } from "../AppMenuPopover";
import { AppMenuList } from "../AppMenuList";
import { MenuItemShim } from "../AppMenuItem/MenuItemShim";
import type { MenuProps } from "@fluentui/react-components";
import { ReactUiPaintTiming } from "@1js/acui-paint-timing";
import { AppMenu } from "./AppMenu";
import type { RefObject } from "@fluentui/utilities";
import { elementContains } from "@fluentui/utilities";
import { FluentProvider } from "@fluentui/react-components";
import { AppThemeManager, AppThemeProvider } from "@1js/acui-theme";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";
import { AppContext } from "@1js/acui-context";

/**
 * Render the final JSX of AppContextualMenu
 */
export const renderAppContextualMenu = (state: AppContextMenuState) => {
  const { appMenuItems, shortcutMetadata } = state;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const appContext = React.useContext(AppContext);

  const onOpenChange: MenuProps["onOpenChange"] = (e, data) => {
    if (
      state.preventDismissOnTargetClick &&
      data.type === "clickOutside" &&
      elementContains(
        (state.target as RefObject<Element>)?.current as HTMLElement,
        data.event.target as HTMLElement
      )
    ) {
      // If we are clicking on the target button then in this case
      // dismiss will be handled by the button click. We do not need
      // to call onDismiss from here.
      return;
    }

    // There are scenarios where V9 CM gets auto-dismissed because of focus being taken away by the app code while CM is opened.
    // VSO 8805655: Fluent V9 ContextMenu for Word ruler is unstable-
    // There is ongoing investigation on app side to prevent focus switch during CM open scenarios.
    // Adding this workaround to fix the issue unless we have the app side fix available.
    if (
      state.isV9MenuAutoDismissFixEnabled &&
      state.calloutProps?.preventDismissOnLostFocus &&
      data.type === "clickOutside"
    ) {
      if (state.listRef.current) {
        // Since the focus is being taken away from menu, put focus back on the first menu item.
        state.findFirstFocusable(state.listRef.current)?.focus();
      }

      return;
    }

    if (!data.open && !(e && (e as React.KeyboardEvent).key === "Tab")) {
      state.onDismiss?.(e, true);
    }
  };
  const headlessThemeEnabled = !!checkExperiment(
    appContext,
    ribbonExperiments.headlessThemingEnabled
  );

  /**
   * Check if any item in the menu has an icon or iconProps.
   * This to avoid extra icon space in the menu when no icons are present in any of the menu item.
   */
  const hasAnyItemWithIcon = (appMenuItems || []).some(
    (item) =>
      item.icon ||
      item.iconProps ||
      (Array.isArray(item.sectionProps?.items) &&
        item.sectionProps.items.some(
          (section) => section.icon || section.iconProps
        ))
  );

  const children = (
    <>
      <ReactUiPaintTiming
        elementTimingId={state.id}
        hidden={state.hidden || state.positionAndHide}
        interactivityTrackerContext={state.interactivityTrackerContext}
      />

      <AppMenu
        open={state.isDispatchOptimizationEnabled ? true : state.open}
        positioning={state.positioning}
        onOpenChange={
          state.hidden && state.isDispatchOptimizationEnabled
            ? undefined
            : onOpenChange
        }
        hasIcons={hasAnyItemWithIcon}
        position={state.menuPosition}
        dataUniqueId={"v9-context-menu"}
      >
        <AppMenuPopover
          {...state.appMenuPopoverProps}
          className={
            (state.hidden && state.isDispatchOptimizationEnabled) ||
            state.positionAndHide
              ? state.hiddenMenuStyle?.root
              : undefined
          }
        >
          <AppMenuList ref={state.listRef} {...state.appMenuListProps}>
            {appMenuItems?.map((item) => (
              // eslint-disable-next-line react/jsx-key
              <MenuItemShim
                itemProps={item}
                shortcutMetadata={shortcutMetadata}
                onDismiss={state.onDismiss}
              />
            ))}
          </AppMenuList>
        </AppMenuPopover>
      </AppMenu>
    </>
  );

  return (
    /**
     * The FluentProvider is used to provide the theme to the AppMenu. This is a temporary mitigation
     * workaround and not a reference to follow in other code.
     * Details in VSO#8725244.
     */
    headlessThemeEnabled ? (
      <AppThemeProvider>{children}</AppThemeProvider>
    ) : (
      <FluentProvider theme={AppThemeManager.getInstance().getThemeNext()}>
        {children}
      </FluentProvider>
    )
  );
};
